import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ContactRequest,
  ContactRequestDecisionPayload,
  CreateContactRequestPayload,
  PageResponse,
  ProfileSearchFilters,
  PublicProfile,
} from '../models';

/**
 * Réseau / mise en relation (UC-15, UC-36), porté par profile-service : annuaire public
 * (ProfileAdminController.searchPublic/publicProfile, ouvert à tout utilisateur authentifié,
 * pas seulement aux admins malgré le nom de la classe) + demandes de contact (ProfileMeController).
 *
 * Volontairement PAS branché sur matching-service (/matching/me/profiles, /connections/suggestions) :
 * ces endpoints réutilisent les lignes de MatchResult "opportunité" du sujet connecté et renvoient
 * son propre profileId comme "profil recommandé" — proposer de se connecter à soi-même. Le vrai
 * annuaire ci-dessous est la source de données correcte tant que ce bug n'est pas corrigé côté backend.
 */
@Injectable({ providedIn: 'root' })
export class NetworkService {
  private readonly http = inject(HttpClient);
  private readonly profilesUrl = `${environment.apiUrl}/profile-service/api/v1/profiles`;
  private readonly meUrl = `${environment.apiUrl}/profile-service/api/v1/profiles/me`;

  searchProfiles(filters: ProfileSearchFilters = {}): Observable<PageResponse<PublicProfile>> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value as string | number);
      }
    });
    return this.http.get<PageResponse<PublicProfile>>(`${this.profilesUrl}/search`, { params });
  }

  getPublicProfile(profileId: string): Observable<PublicProfile> {
    return this.http.get<PublicProfile>(`${this.profilesUrl}/public/${profileId}`);
  }

  sendContactRequest(targetProfileId: string, payload: CreateContactRequestPayload): Observable<ContactRequest> {
    return this.http.post<ContactRequest>(`${this.meUrl}/${targetProfileId}/contact-requests`, payload);
  }

  sentRequests(page = 0, size = 20): Observable<PageResponse<ContactRequest>> {
    const params = new HttpParams().set('page', page).set('size', size).set('sort', 'createdAt,desc');
    return this.http.get<PageResponse<ContactRequest>>(`${this.meUrl}/contact-requests/sent`, { params });
  }

  receivedRequests(page = 0, size = 20): Observable<PageResponse<ContactRequest>> {
    const params = new HttpParams().set('page', page).set('size', size).set('sort', 'createdAt,desc');
    return this.http.get<PageResponse<ContactRequest>>(`${this.meUrl}/contact-requests/received`, { params });
  }

  acceptRequest(contactRequestId: string, payload: ContactRequestDecisionPayload = {}): Observable<ContactRequest> {
    return this.http.patch<ContactRequest>(`${this.meUrl}/contact-requests/${contactRequestId}/accept`, payload);
  }

  rejectRequest(contactRequestId: string, payload: ContactRequestDecisionPayload = {}): Observable<ContactRequest> {
    return this.http.patch<ContactRequest>(`${this.meUrl}/contact-requests/${contactRequestId}/reject`, payload);
  }

  cancelRequest(contactRequestId: string, payload: ContactRequestDecisionPayload = {}): Observable<ContactRequest> {
    return this.http.patch<ContactRequest>(`${this.meUrl}/contact-requests/${contactRequestId}/cancel`, payload);
  }
}
